/** Searchable palette of fields and layout templates available to the designer. */
import { useMemo, useState, type DragEvent } from 'react';
import type { DynamicFormDefinition, FieldType, InputType } from '../../models/form';
import { clearDesignerDrag, writeDesignerDrag, type DesignerDragPayload } from './drag-payload';
import type { DesignerSelection } from './designer-utils';
import { LayersTree } from './LayersTree';
import { useAccess } from '../access/AccessContext';

interface PaletteItem {
  id: string;
  type: FieldType;
  inputType?: InputType;
  title: string;
  icon: string;
  keywords?: string;
}

const groups: Array<{ title: string; items: PaletteItem[] }> = [
  { title: 'کنترل‌های فرم', items: [
    { id: 'input-text', type: 'input', inputType: 'text', title: 'جعبه متنی', icon: 'input', keywords: 'input textbox text متن ورودی' },
    { id: 'input-number', type: 'input', inputType: 'number', title: 'فیلد عددی', icon: 'input', keywords: 'number numeric decimal integer عدد عددی اعشاری صحیح' },
    { id: 'textarea', type: 'textarea', title: 'ناحیه متنی', icon: 'textarea', keywords: 'textarea multiline متن چندخطی' },
    { id: 'select', type: 'select', title: 'جعبه کشویی', icon: 'select', keywords: 'select dropdown list انتخاب' },
    { id: 'checkbox', type: 'checkbox', title: 'چک‌باکس', icon: 'checkbox', keywords: 'check boolean بله خیر' },
    { id: 'radio', type: 'radio', title: 'رادیوباکس', icon: 'radio', keywords: 'radio option گزینه' },
    { id: 'table', type: 'table', title: 'جدول اطلاعاتی', icon: 'table', keywords: 'table grid row جدول ردیف' },
    { id: 'fileupload', type: 'fileupload', title: 'آپلود فایل', icon: 'fileupload', keywords: 'file upload attachment فایل پیوست' },
    { id: 'simplecaptcha', type: 'simplecaptcha', title: 'کپچای داخلی', icon: 'simplecaptcha', keywords: 'captcha security امنیتی غیرگوگل فارسی' },
    { id: 'captcha', type: 'captcha', title: 'Google Captcha', icon: 'captcha', keywords: 'recaptcha google کپچا' }
  ]},
  { title: 'کنترل‌های عمومی', items: [
    { id: 'text', type: 'text', title: 'متن و HTML', icon: 'p', keywords: 'paragraph html rich text متن' },
    { id: 'image', type: 'image', title: 'تصویر', icon: 'image', keywords: 'image picture عکس' },
    { id: 'panel', type: 'panel', title: 'پنل', icon: 'panel', keywords: 'panel group container گروه' },
    { id: 'spacer', type: 'spacer', title: 'فاصله', icon: 'spacer', keywords: 'space separator فاصله' },
    { id: 'button', type: 'button', title: 'دکمه', icon: 'button', keywords: 'button submit next previous دکمه ارسال' }
  ]},
  { title: 'نمایشگرها', items: [
    { id: 'display-datetime', type: 'display-datetime', title: 'تاریخ و زمان', icon: 'display-datetime', keywords: 'date time clock تاریخ زمان' },
    { id: 'display-label', type: 'display-label', title: 'برچسب متصل', icon: 'display-label', keywords: 'label bind value برچسب مقدار' }
  ]}
];


const itemFeature: Record<string, string | undefined> = {
  table: 'Designer.RepeatableTable', fileupload: 'Upload.File', captcha: 'Validation.Recaptcha', simplecaptcha: 'Captcha.Simple',
  text: 'Designer.RichContent', image: 'Designer.RichContent', spacer: 'Designer.RichContent', button: 'Designer.RichContent',
  panel: 'Designer.Panel', 'display-label': 'Designer.DisplayControls', 'display-datetime': 'Designer.DisplayControls'
};

interface Props {
  form: DynamicFormDefinition;
  activeTab: string;
  selection?: DesignerSelection;
  onAddField: (type: FieldType, inputType?: InputType) => void;
  onAddLayout: (columns: number) => void;
  onChangeTab: (tabName: string) => void;
  onSelect: (selection: DesignerSelection) => void;
}

function normalize(value: string): string {
  return value.trim().toLocaleLowerCase('fa-IR');
}

function beginPaletteDrag(event: DragEvent<HTMLElement>, payload: DesignerDragPayload): void {
  writeDesignerDrag(event, payload, 'copy');
  event.currentTarget.classList.add('is-dragging');
}

function endPaletteDrag(event: DragEvent<HTMLElement>): void {
  clearDesignerDrag();
  event.currentTarget.classList.remove('is-dragging');
}

export function Palette({ form, activeTab, selection, onAddField, onAddLayout, onChangeTab, onSelect }: Props) {
  const { hasFeature } = useAccess();
  const [mode, setMode] = useState<'tools' | 'layers'>('tools');
  const [term, setTerm] = useState('');
  const filteredGroups = useMemo(() => {
    const needle = normalize(term);
    const available = groups.map((group) => ({ ...group, items: group.items.filter((item) => !itemFeature[item.id] || hasFeature(itemFeature[item.id]!)) })).filter((group) => group.items.length);
    if (!needle) return available;
    return available.map((group) => ({
      ...group,
      items: group.items.filter((item) => normalize(`${item.title} ${item.type} ${item.keywords ?? ''}`).includes(needle))
    })).filter((group) => group.items.length);
  }, [hasFeature, term]);

  return <aside className="df-designer-palette" aria-label="ابزارها و لایه‌های طراحی فرم">
    <div className="df-palette-mode-tabs" role="tablist" aria-label="نمای پنل کناری">
      <button type="button" role="tab" aria-selected={mode === 'tools'} className={mode === 'tools' ? 'active' : ''} onClick={() => setMode('tools')}>ابزارها</button>
      <button type="button" role="tab" aria-selected={mode === 'layers'} className={mode === 'layers' ? 'active' : ''} onClick={() => setMode('layers')}>درخت لایه‌ها</button>
    </div>
    {mode === 'layers' ? <LayersTree form={form} activeTab={activeTab} selection={selection} onChangeTab={onChangeTab} onSelect={onSelect} /> : <>
      <div className="df-palette-header">
        <h3>ابزارها</h3>
        <p>با کلیک اضافه کنید یا به محل دلخواه بکشید.</p>
        <div className="df-palette-search">
          <span aria-hidden="true">⌕</span>
          <input className="form-control input-sm" value={term} onChange={(event) => setTerm(event.target.value)} placeholder="جستجوی کنترل…" aria-label="جستجوی کنترل" />
          {term ? <button type="button" onClick={() => setTerm('')} aria-label="پاک‌کردن جستجو">×</button> : null}
        </div>
      </div>
      {!term ? <section><h4>لایه‌ها</h4><div className="df-palette-grid">
        {[1, 2, 3, 4].filter((columns) => columns === 1 || hasFeature('Designer.MultiColumn')).map((columns) => <button key={columns} type="button" draggable data-df-drag-source="layout" title={`${columns} ستون؛ کلیک یا درگ`} onDragStart={(event) => beginPaletteDrag(event, { kind: 'layout', columns })} onDragEnd={endPaletteDrag} onClick={() => onAddLayout(columns)}>
          <img draggable={false} src={`/DesktopModules/FaraDynamicForms/Contents/assets/img/dashboard/layouts/col-${columns}.png`} alt="" /><span>{`${columns} ستون`}</span>
        </button>)}
      </div></section> : null}
      {filteredGroups.map((group) => <section key={group.title}><h4>{group.title}</h4><div className="df-palette-grid">
        {group.items.map((item) => <button key={item.id} type="button" draggable data-df-drag-source="field" title={`${item.title}؛ کلیک یا درگ`} onDragStart={(event) => beginPaletteDrag(event, { kind: 'field', type: item.type, inputType: item.inputType })} onDragEnd={endPaletteDrag} onClick={() => onAddField(item.type, item.inputType)}>
          <img draggable={false} src={`/DesktopModules/FaraDynamicForms/Contents/assets/img/dashboard/components/${item.icon}.png`} alt="" /><span>{item.title}</span>
        </button>)}
      </div></section>)}
      {term && !filteredGroups.length ? <div className="df-empty-settings">کنترلی با این عبارت پیدا نشد.</div> : null}
    </>}
  </aside>;
}
